import React, { Fragment, useEffect, useRef, useState, createContext, useContext } from "react"
import { MapContext } from "../context"
import { MapEventEnum } from "../enum"
// createRoot seems unused, removing for cleanup
// import { createRoot } from "react-dom/client"

// Removed commented-out import

// Declare type for the window object
declare global {
  interface Window {
    ngm: any
  }
}

// Assign to window object
/* tslint:disable-next-line */
// window.ngm = window.ngm.map // Removed unused assignment

const createId = (): string => {
  let dt = new Date().getTime()
  const uuid = "xxxxxxxx_xxxx_4xxx_yxxx_xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (dt + Math.random() * 16) % 16 | 0
    dt = Math.floor(dt / 16)
    return (c == "x" ? r : (r & 0x3) | 0x8).toString(16)
  })
  return uuid
}

interface MapOptions {
  center?: { lat: number; lng: number }
  zoom?: number
  minZoom?: number
  maxZoom?: number
  maxNativeZoom?: number
  geolocate?: boolean
  tilt?: number
  bearing?: number
  controls?: boolean
  mapType?: string
  controlOptions?: object
  restrictionBounds?: { north: number; south: number; east: number; west: number }
  shouldChangeMapMode?: (mode: string) => void
  cooperativeGestures?: boolean
}

interface MapProps {
  mapid?: string
  options: MapOptions
  version: string
  accessKey: string
  environment?: "dev" | "test" | undefined | null
  sdkDomain?: string | null
  onMapReady?: (map: any, key?: string) => void
  onClickLocation?: (args: { lat: number; lng: number }) => void
  onRightClickLocation?: (args: { lat: number; lng: number }) => void
  onCameraChanging?: (args: { center: { lat: number; lng: number }; zoom: number }) => void
  onTilesLoaded?: () => void
  children?: React.ReactNode
}

// Define the types for the global Woosmap object
declare const ngm: any

// Custom hook to load the Woosmap JavaScript API
// This hook creates a script tag with the Woosmap map.js URL and appends it to the body of the document.
// It sets the state to true when the script loads.
function useNgm(apiKey?: string) {
  const [isLoaded, setIsLoaded] = useState(false)

  useEffect(() => {
    const sdkUrl = process.env.REACT_APP_SDK_URL || "https://nextgenmap.com"
    const scriptSrc = `${sdkUrl}/sdk.js`
    
    // Check if script already exists
    const existingScript = document.querySelector(`script[src="${scriptSrc}"]`)
    
    if (existingScript) {
      // Script already exists, check if it's loaded
      if (window.ngm?.map) {
        setIsLoaded(true)
      } else {
        // Script exists but not loaded yet, wait for it
        existingScript.addEventListener("load", () => setIsLoaded(true))
      }
      return
    }

    // Create new script only if it doesn't exist
    const script = document.createElement("script")
    script.src = scriptSrc
    script.onload = () => {
      setIsLoaded(true)
    }
    document.body.appendChild(script)
  }, [apiKey])

  return isLoaded
}

interface NextGenMapAPIProviderProps {
  apiKey?: string
  children: React.ReactNode
}

// This component uses the useWoosmap hook to load the Woosmap API and provides it through context.
const NextGenMapAPIProvider: React.FC<NextGenMapAPIProviderProps> = ({ children }) => {
  const isLoaded = useNgm()

  if (!isLoaded) {
    return null
  }

  return <MapContext.Provider value={window.ngm.map}>{children}</MapContext.Provider>
}

// Removed NextGenMapAPIProvider as it's no longer rendered internally

// Properly type the functional component and remove @ts-ignore
const MFMap: React.FC<MapProps> = (props) => {
  const id = createId()
  const mapRef = useRef<HTMLDivElement>(null) // Add type hint for the ref
  // Use the hook directly to manage script loading
  const isLoaded = useNgm()
  const [mapInstance, setMapInstance] = useState<any>(null) // Added type hint
  const events = useRef<any[]>([])

  // Effect runs when the script is loaded and the map isn't initialized yet
  useEffect(() => {
    // Ensure the script is loaded, ref exists, and map isn't already created
    if (isLoaded && mapRef.current && !mapInstance && window.ngm?.map?.Map) {
      const map = new window.ngm.map.Map(mapRef.current, {
        zoom: props.options?.zoom ?? 5, // Use options from props or default
        center: props.options?.center ?? { lat: 10.793113, lng: 106.720739 }, // Use options from props or default
        ...props.options // Spread the rest of the options
      })

      registerEvent()

      setMapInstance(map)
      if (props.onMapReady) {
        props.onMapReady(map, id)
      }
      return () => {
        events.current?.forEach((t: any) => {
          t?.remove()
        })
        // map?.destroy()
      }
    }
    // Dependency array includes isLoaded state and props that might change map options
  }, [isLoaded, mapInstance, props.onMapReady, props.options, id])
  const registerEvent = () => {
    const eventClickMarker = mapInstance?.addListener(
      MapEventEnum.click,
      (args: any) => {
        args.marker?.onClick && args.marker?.onClick(args)
      },
      {
        marker: true
      }
    )
    const eventRightClickMarker = mapInstance?.addListener(
      MapEventEnum.rightClick,
      (args: any) => {
        args.marker?.onRightClick && args.marker?.onRightClick(args)
      },
      {
        marker: true
      }
    )
    const eventDragEndMarker = mapInstance?.addListener(
      MapEventEnum.dragEnd,
      (args: any) => {
        args.marker?.onDragEnd && args.marker?.onDragEnd(args)
      },
      {
        marker: true
      }
    )

    const eventHoverMarker = mapInstance?.addListener(
      MapEventEnum.hover,
      (args: any) => {
        args.marker?.onHover && args.marker?.onHover(args)
      },
      {
        marker: true
      }
    )

    const eventHoverPolygon = mapInstance?.addListener(
      MapEventEnum.hover,
      (args: any) => {
        args.polygon?.onHover && args.polygon?.onHover(args)
      },
      {
        polygon: true
      }
    )

    const eventHoverPolyline = mapInstance?.addListener(
      MapEventEnum.hover,
      (args: any) => {
        args.polyline?.onHover && args.polyline?.onHover(args)
      },
      {
        polyline: true
      }
    )

    const eventHoverDirection = mapInstance?.addListener(
      MapEventEnum.hover,
      (args: any) => {
        args.renderer?.onHover && args.renderer?.onHover(args)
      },
      {
        directions: true
      }
    )

    const eventClickPolyline = mapInstance?.addListener(
      MapEventEnum.click,
      (args: any) => {
        args.polyline?.onClick && args.polyline?.onClick(args)
      },
      {
        polyline: true
      }
    )

    const eventMouseOutDirection = mapInstance?.addListener(
      MapEventEnum.mouseOut,
      (args: any) => {
        args.renderer?.onMouseOut && args.renderer?.onMouseOut(args)
      },
      {
        directions: true
      }
    )

    const eventMouseOverDirection = mapInstance?.addListener(
      MapEventEnum.mouseOver,
      (args: any) => {
        args.renderer?.onMouseOver && args.renderer?.onMouseOver(args)
      },
      {
        directions: true
      }
    )

    const eventMouseMoveDirection = mapInstance?.addListener(
      MapEventEnum.mouseMove,
      (args: any) => {
        args.renderer?.onMouseMove && args.renderer?.onMouseMove(args)
      },
      {
        directions: true
      }
    )

    const eventMouseOutPolyline = mapInstance?.addListener(
      MapEventEnum.mouseOut,
      (args: any) => {
        args.polyline?.onMouseOut && args.polyline?.onMouseOut(args)
      },
      {
        polyline: true
      }
    )

    const eventMouseOverPolyline = mapInstance?.addListener(
      MapEventEnum.mouseOver,
      (args: any) => {
        args.polyline?.onMouseOver && args.polyline?.onMouseOver(args)
      },
      {
        polyline: true
      }
    )

    const eventMouseMovePolyline = mapInstance?.addListener(
      MapEventEnum.mouseMove,
      (args: any) => {
        args.polyline?.onMouseMove && args.polyline?.onMouseMove(args)
      },
      {
        polyline: true
      }
    )

    const eventMouseOutMarker = mapInstance?.addListener(
      MapEventEnum.mouseOut,
      (args: any) => {
        args.marker?.onMouseOut && args.marker?.onMouseOut(args)
      },
      {
        marker: true
      }
    )

    const eventMouseOverMarker = mapInstance?.addListener(
      MapEventEnum.mouseOver,
      (args: any) => {
        args.marker?.onMouseOver && args.marker?.onMouseOver(args)
      },
      {
        marker: true
      }
    )

    const eventMouseMoveMarker = mapInstance?.addListener(
      MapEventEnum.mouseMove,
      (args: any) => {
        args.marker?.onMouseMove && args.marker?.onMouseMove(args)
      },
      {
        marker: true
      }
    )

    const eventClickPoi = mapInstance?.addListener(
      MapEventEnum.click,
      (args: any) => {
        args.poi?.onClick && args.poi?.onClick(args)
      },
      {
        poi: true
      }
    )

    const eventHoverPoi = mapInstance?.addListener(
      MapEventEnum.hover,
      (args: any) => {
        args.poi?.onHover && args.poi?.onHover(args)
      },
      {
        poi: true
      }
    )
    events.current = events.current.concat([
      eventClickMarker,
      eventRightClickMarker,
      eventDragEndMarker,
      eventHoverMarker,
      eventHoverPolygon,
      eventHoverPolyline,
      eventHoverDirection,
      eventClickPolyline,
      eventMouseOutDirection,
      eventMouseOverDirection,
      eventMouseMoveDirection,
      eventMouseOutPolyline,
      eventMouseOverPolyline,
      eventMouseMovePolyline,
      eventMouseOutMarker,
      eventMouseOverMarker,
      eventMouseMoveMarker,
      eventClickPoi,
      eventHoverPoi
    ])
    if (props.onClickLocation) {
      const onClickLocation = mapInstance?.addListener(
        MapEventEnum.click,
        (args: { lat: number; lng: number }) => {
          props.onClickLocation && props.onClickLocation(args)
        },
        {
          location: true
        }
      )
      events.current.push(onClickLocation)
    }
    if (props.onRightClickLocation) {
      const onRightClickLocation = mapInstance?.addListener(
        MapEventEnum.rightClick,
        (args: { lat: number; lng: number }) => {
          props.onRightClickLocation && props.onRightClickLocation(args)
        },
        {
          location: true
        }
      )
      events.current.push(onRightClickLocation)
    }
    // if (props.onTilesLoaded) {
    //   const onTilesLoaded = mapInstance?.addListener(
    //     MapEventEnum.tilesLoaded,
    //     (args: any) => {
    //       props.onTilesLoaded && props.onTilesLoaded(args)
    //     },
    //     {
    //       location: true
    //     }
    //   )
    //   events.current.push(onTilesLoaded)
    // }
    if (props.onCameraChanging) {
      const onCameraChanging = mapInstance?.addListener(
        MapEventEnum.cameraChanging,
        (args: { center: { lat: number; lng: number }; zoom: number }) => {
          props.onCameraChanging && props.onCameraChanging(args)
        },
        {
          location: true
        }
      )
      events.current.push(onCameraChanging)
    }
  }

  // Render the map container div. Children are passed through.
  return (
    <div style={{ width: "100%", height: "100%", display: "block" }} ref={mapRef}>
      <NextGenMapAPIProvider>
        {/* Render children only after the map instance is ready, potentially passing map context */}
        {/* Or simply render children immediately if they don't depend on the map */}
        {mapInstance && props.children}
        {/* If children need map context, a different pattern (like providing context here) is needed */}
      </NextGenMapAPIProvider>
    </div>
  )
}

export default MFMap
